// --- src/data/m3/Unit3.js ---

export const M3_UNIT_3 = {
  id: "m3-u3-describing-things",
  title: "Unit 3: Describing Things",
  topic: "Opinion Adjectives & Adjective Order",
  iconType: "star",
  grammarImage: "/m3-u3-adjectives.jpg",
  grammarText: "Unit focus: opinion adjectives (beautiful, horrible, boring) + adjective order (opinion BEFORE fact) + but (contrast) + because (reason) + materials (wooden, leather).",
  sets: [

    // ===================================================================
    // SET 1: OPINION ADJECTIVES - POSITIVE (A1)
    // ===================================================================
    {
      id: "m3-u3-set1",
      title: "Set 1: Beautiful Things",
      context: `*Lunes por la mañana - Casa de Emma*

Emma despierta y mira por la ventana. "Que hermoso dia!" piensa.
Abre su armario y busca ropa. "Me encanta este hermoso vestido" dice mientras la toca.

*Monday morning - Emma's house*

Emma wakes up and looks out the window. "What a beautiful day!" she thinks.
She opens her closet and looks for clothes. "I love this lovely dress," she says as she touches it.`,
      
      questions: [
        {
          type: "match",
          q: "Match the positive opinion adjectives to their meanings:",
          pairs: [
            { left: "Beautiful", right: "Very pretty" },
            { left: "Lovely", right: "Very nice" },
            { left: "Great", right: "Very good" }
          ],
          a: "completed",
          successTip: "POSITIVE OPINIONS! Beautiful, lovely, great = happy words! 😍✨"
        },
        {
          type: "mc",
          q: "Emma looks at her garden: 'This is a _____ flower! I love it!'",
          options: ["beautiful", "horrible", "boring"],
          a: "beautiful",
          tip: "Beautiful = very pretty."
        },
        {
          type: "order",
          q: "Traduce: 'Este es un sombrero encantador.'",
          words: ["This", "is", "a", "lovely", "hat."],
          a: "This is a lovely hat.",
          extra: ["horrible", "beautiful", "boring"],
          tip: "Lovely = encantador, lindo."
        },
        {
          type: "input",
          q: "Emma thinks about the party: 'What a _____ party last night!' (divertido)",
          a: "fun",
          alts: ["great", "lovely"],
          successTip: "FUN = divertido! UK: 'brilliant', US: 'awesome' too! 🎉"
        },
        {
          type: "match",
          q: "Match more positive adjectives:",
          pairs: [
            { left: "Nice", right: "Pleasant/good" },
            { left: "Wonderful", right: "Amazing" },
            { left: "Brilliant", right: "Excellent" }
          ],
          a: "completed",
          successTip: "All express positive opinions!"
        },
        {
          type: "mc",
          q: "Emma sees her friend's new car: 'You have a _____ new car! It's awesome!'",
          options: ["great", "horrible", "ugly"],
          a: "great",
          successTip: "GREAT! UK: 'brilliant', US: 'awesome'! Versatile word! 🚗"
        },
        {
          type: "input",
          q: "Emma pets a dog in the park: 'It's a _____ dog.'",
          a: "lovely",
          alts: ["beautiful", "nice", "cute", "adorable", "pretty", "great", "fun"],
          tip: "Lovely works for animals, people, things!",
          successTip: "To pet a dog significa acariciar un perro!🐶❤️"
        },
        {
          type: "order",
          q: "Traduce: 'Mis vecinos tienen un jardín bonito.'",
          words: ["My","neighbors","have", "a", "nice", "garden."],
          a: "My neighbors have a nice garden.",
          extra: ["horrible", "ugly", "we"],
          tip: "Nice = versatile positive word.",
          successTip: "Us:Neighbors, Uk:Neighbours! Spelling matters!🌸"
        },
        {
          type: "mc",
          q: "Emma finishes a good book: 'This book is _____!'",
          options: ["brilliant", "boring", "terrible"],
          a: "brilliant",
          successTip: "BRILLIANT! UK favorite! US: 'excellent/amazing'! 📚✨"
        },
        {
          type: "input",
          q: "Emma describes her vacation: 'We have a _____ time!' 😊",
          a: "wonderful",
          alts: ["great", "lovely", "brilliant", "fun", "nice", "awesome", "amazing", "excellent", "beautiful"],
          successTip: "WONDERFUL TIME! Perfect for experiences! 😊"
        }
      ]
    },

    // ===================================================================
    // SET 2: OPINION ADJECTIVES - NEGATIVE (A1)
    // ===================================================================
    {
      id: "m3-u3-set2",
      title: "Set 2: Not So Nice",
      context: `*Martes por la tarde - Centro de la ciudad*

Emma camina por la ciudad y ve diferentes cosas.
"Ese es un edificio feo" piensa cuando pasa un edificio viejo.
Entra a un cine y ve un cartel: "Este filme parece aburrido."

*Tuesday afternoon - City center*

Emma walks through the city and sees different things.
"That's an ugly building," she thinks when passing an old building.
She enters a cinema and sees a poster: "This movie looks boring."`,
      
      questions: [
        {
          type: "match",
          q: "Match the negative opinion adjectives:",
          pairs: [
            { left: "Horrible", right: "Very bad" },
            { left: "Ugly", right: "Not pretty" },
            { left: "Boring", right: "Not interesting" }
          ],
          a: "completed",
          successTip: "NEGATIVE OPINIONS! Use carefully - they're strong! 😬⚠️"
        },
        {
          type: "mc",
          q: "Emma watches a movie: 'This is a _____ movie. I'm falling asleep!'",
          options: ["boring", "lovely", "fun"],
          a: "boring",
          tip: "Boring = not interesting, tedious."
        },
        {
          type: "order",
          q: "Traduce: 'Ese es un suéter feo.'",
          words: ["That's", "an", "ugly", "sweater."],
          a: "That's an ugly sweater.",
          tip: "Ugly = not attractive."
        },
        {
          type: "input",
          q: "Emma sees ugly old shoes: 'These shoes are _____!'",
          a: "horrible",
          alts: ["ugly", "terrible", "bad", "awful", "horrible", "weird", "strange"],
          successTip: "HORRIBLE! Very strong negative! UK/US both use it! 👞😱"
        },
        {
          type: "match",
          q: "Match more negative adjectives:",
          pairs: [
            { left: "Terrible", right: "Very bad" },
            { left: "Awful", right: "Really bad" },
            { left: "Bad", right: "Not good" }
          ],
          a: "completed",
          successTip: "Awful es mas una opinion personal subjetiva y Terrible puede ser opinion pero mas objetiva."
        },
        {
          type: "mc",
          q: "Emma tries food at a restaurant: 'This food is _____!'",
          options: ["terrible", "delicious", "great"],
          a: "terrible",
          tip: "Terrible = very bad.",
          successTip: "Terrible se usa como opinion objetiva 'de mala calidad' 🍽️😖"
        },
        {
          type: "input",  
          q: "Emma looks outside at the rain: 'What a _____ day!'",
          a: "bad",
          alts: ["horrible", "terrible", "awful"],
          successTip: "BAD DAY! UK: 'rubbish day', US: 'crappy day' (informal)! ☔"
        },
        {
          type: "order",
          q: "Traduce: 'Es una fiesta aburrida.'",
          words: ["It's", "a", "boring", "party."],
          a: "It's a boring party.",
          extra: ["lovely", "great", "fun"],
          tip: "Boring = aburrido."
        },
        {
          type: "mc",
          q: "What's the opposite of 'beautiful'?",
          options: ["ugly", "lovely", "nice"],
          a: "ugly",
          successTip: "OPPOSITES! Beautiful↔Ugly! Learn pairs! 🔄"
        },
        {
          type: "input",
          q: "Emma hears a suggestion: 'That's an _____ idea!'",
          a: "awful",
          alts: ["terrible", "horrible", "bad"],
          successTip: "AWFUL IDEA! Now you know positive AND negative opinions! ✅"
        }
      ]
    },

    // ===================================================================
    // SET 3: ADJECTIVE ORDER BASICS (A2)
    // ===================================================================
    {
      id: "m3-u3-set3",
      title: "Set 3: The Right Order",
      context: `*Miércoles - Tienda de ropa*

Emma va de compras con su amiga Lucy.
"Quiero un vestido verde hermoso" dice Emma.
Lucy la corrige: "Recuerda: ¡opinión antes del color!"

*Wednesday - Clothing store*

Emma goes shopping with her friend Lucy.
"I want a green beautiful dress," says Emma.
Lucy corrects her: "Remember: opinion before color!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the adjective type to its position:",
          pairs: [
            { left: "Opinion (beautiful)", right: "FIRST" },
            { left: "Fact (green)", right: "SECOND" },
            { left: "Noun (hat)", right: "LAST" }
          ],
          a: "completed",
          successTip: "ORDER RULE! Opinion→Fact→Noun! Beautiful green hat! 🎯📏"
        },
        {
          type: "mc",
          q: "Emma describes a hat. Which is correct?",
          options: ["a lovely green hat", "a green lovely hat"],
          a: "a lovely green hat",
          tip: "Opinion (lovely) comes BEFORE fact (green)."
        },
        {
          type: "order",
          q: "Traduce: 'Es un vestido rojo hermoso.'",
          words: ["It's", "a", "beautiful", "red", "dress."],
          a: "It's a beautiful red dress.",
          extra: ["skirt", "this", "green"],
          tip: "Opinion (beautiful) + fact (red).",
          successTip: "PERFECT! Beautiful red dress! Opinion first! 👗✨"
        },
        {
          type: "input",
          q: "Emma wants a car: 'I have a _____ _____ car.' (genial/nuevo)",
          a: "great new",
          tip: "Great (opinion) before new (age/fact)."
        },
        {
          type: "mc",
          q: "Lucy describes a car. Which is correct?",
          options: ["a horrible old car", "an old horrible car","a car horrible old"],
          a: "a horrible old car",
          tip: "HORRIBLE OLD CAR! Opinion (horrible) + age (old)! 🚗"
        },
        {
          type: "order",
          q: "Traduce: 'Esta es una gran casa encantadora.'",
          words: ["This", "is", "a", "lovely", "big", "house."],
          a: "This is a lovely big house.",
          extra: ["that", "ugly", "small"],
          tip: "Opinion (lovely) + size (big)."
        },
        {
          type: "input",
          q: "Emma sees a dog: 'They have an _____ _____ dog.' (hermoso/negro)",
          a: "beautiful black",
          alts: ["lovely black", "great black","awesome black"],
          tip: "Opinion (beautiful) + color (black)."
        },
        {
          type: "mc",
          q: "Which order is correct?",
          options: ["a boring long movie", "a long boring movie","a movie boring long"],
          a: "a boring long movie",
          successTip: "BORING LONG MOVIE! Opinion before size/length! 🎬"
        },
        {
          type: "order",
          q: "Traduce: 'Ella tiene un gato viejo hermoso.'",
          words: ["She", "has", "a", "beautiful", "old", "cat."],
          a: "She has a beautiful old cat.",
          tip: "Opinion (beautiful) + age (old)."
        },
        {
          type: "input",
          q: "Emma finds a book: 'It's a _____ _____ book.' (genial/nuevo)",
          a: "great new",
          successTip: "GREAT NEW BOOK! Opinion + age! You've got the order! 📚✅"
        }
      ]
    },

    // ===================================================================
    // SET 4: MATERIALS (A2)
    // ===================================================================
    {
      id: "m3-u3-set4",
      title: "Set 4: What's It Made Of?",
      context: `*Jueves - Tienda de muebles*

Emma busca una mesa nueva para su cocina.
"Esta mesa de madera es hermosa" dice mientras toca la madera.
El vendedor le muestra otra: "Tambien tenemos mesas de plástico. Son más baratas."

*Thursday - Furniture store*

Emma looks for a new table for her kitchen.
"This wooden table is beautiful," she says as she touches the wood.
The salesman shows her another: "We also have plastic tables. They're cheaper."`,
      
      questions: [
        {
          type: "match",
          q: "Match the material to its adjective form:",
          pairs: [
            { left: "Wood", right: "Wooden" },
            { left: "Wool", right: "Woolen" },
            { left: "Plastic", right: "Plastic" }
          ],
          a: "completed",
          successTip: "MATERIAL RULE! Wood→Wooden, Wool→Woolen! Others stay same! 🪵"
        },
        {
          type: "mc",
          q: "Emma touches a table: 'This is a _____ table.'",
          options: ["wooden", "wood", "woods"],
          a: "wooden",
          tip: "Wood (noun) → wooden (adjective)."
        },
        {
          type: "order",
          q: "Traduce: 'Tengo una silla de plástico.'",
          words: ["I", "have", "a", "plastic", "chair."],
          a: "I have a plastic chair.",
          tip: "Plastic stays the same!"
        },
        {
          type: "input",
          q: "Emma sees a sweater: 'It's a _____ sweater.'(de lana)",
          a: "woolen",
          alts: ["wool"],
          successTip: "WOOLEN! Wool (noun) → woolen (adjective)! 🧶"
        },
        {
          type: "match",
          q: "Match more materials:",
          pairs: [
            { left: "Leather", right: "Cuero" },
            { left: "Metal", right: "Metal" },
            { left: "Glass", right: "Vidrio" }
          ],
          a: "completed",
          successTip: "These materials don't change form! Leather, metal, glass = same! 👜🔩🍷"
        },
        {
          type: "mc",
          q: "Emma looks at a jacket: 'She has a _____ jacket.'",
          options: ["leather", "leathern", "leathers"],
          a: "leather",
          tip: "Leather doesn't change!"
        },
        {
          type: "input",
          q: "Emma sees chairs: 'We have _____ chairs. They're very strong. Better than plastic'",
          a: "metal",
          alts: ["metals","wooden","wood"],
          tip: "Metal stays the same! or you can say wooden chairs.",
          successTip: "Better means: 'mejor que'"
        },
        {
          type: "order",
          q: "Traduce: 'Esta es una mesa de madera hermosa.'",
          words: ["This", "is", "a", "beautiful", "wooden", "table."],
          a: "This is a beautiful wooden table.",
          tip: "Opinion (beautiful) + material (wooden).",
          extra: ["plastic", "ugly", "chair"],
          successTip: "BEAUTIFUL WOODEN TABLE! Opinion + material! 🪵✨"
        },
        {
          type: "match",
          q: "Match the item to its common material:",
          pairs: [
            { left: "Table", right: "Wood" },
            { left: "Bottle", right: "Glass or plastic" },
            { left: "Jacket", right: "Leather or fabric" }
          ],
          a: "completed",
          tip: "Learn common material combinations!"
        },
        {
          type: "input",
          q: "Emma sees a sofa: 'That's an expensive _____ couch.' (de cuero)",
          a: "leather",
          successTip: "LEATHER COUCH! Materials mastered! Next: complex order! 🛋️"
        }
      ]
    },

    // ===================================================================
    // SET 5: FULL ADJECTIVE ORDER (A2+)
    // ===================================================================
    {
      id: "m3-u3-set5",
      title: "Set 5: Complete Descriptions",
      context: `*Viernes - Reunión con amigas*

Emma describe su nueva compra a sus amigas:
"Compre un vestido de algodón rojo encantador ayer!"
Sus amigas aplauden: "Orden perfecto de adjetivos!"

*Friday - Meeting with friends*

Emma describes her new purchase to her friends:
"I bought a lovely red cotton dress yesterday!"
Her friends applaud: "Perfect adjective order!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the complete adjective order:",
          pairs: [
            { left: "Opinion", right: "Beautiful" },
            { left: "Size/Age", right: "Big/Old" },
            { left: "Color/Material", right: "Red/Wooden" }
          ],
          a: "completed",
          successTip: "FULL ORDER! Opinion→Size/Age→Color/Material→Noun! 🎨📏"
        },
        {
          type: "order",
          q: "Traduce: 'Tengo un vestido de algodón rojo encantador.'",
          words: ["I", "have", "a", "lovely", "red", "cotton", "dress."],
          a: "I have a lovely red cotton dress.",
          tip: "Opinion (lovely) + color (red) + material (cotton).",
          successTip: "PERFECT! Lovely red cotton dress! Complete order! 👗"
        },
        {
          type: "mc",
          q: "Emma describes a bag. Which is correct?",
          options: ["a horrible green plastic bag", "a plastic green horrible bag", "a green plastic horrible bag"],
          a: "a horrible green plastic bag",
          tip: "Opinion + color + material."
        },
        {
          type: "input",
          q: "Emma wants a jacket: 'She has a _____ _____ _____ jacket.' (hermosa/café/de cuero)",
          a: "beautiful brown leather",
          tip: "Opinion + color + material."
        },
        {
          type: "order",
          q: "Traduce: 'Mis amigas tienen una computadora portátil nueva genial.'",
          words: ["My", "friends", "have", "a", "great", "new", "laptop."],
          a: "My friends have a great new laptop.",
          extra: ["old", "this", "terrible"],
          tip: "Opinion (great) + age (new)."
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: ["an ugly old sweater", "an old ugly sweater","a sweater old ugly"],
          a: "an ugly old sweater",
          successTip: "UGLY OLD SWEATER! Opinion before age! 🧶"
        },
        {
          type: "input",
          q: "Emma shops for winter: 'I want a nice _____ _____ scarf.' (amarilla/de lana)",
          a: "yellow woolen",
          tip: "Opinion + color + material."
        },
        {
          type: "order",
          q: "Traduce: 'Esta es una película vieja aburrida.'",
          words: ["This", "is", "a", "boring", "old", "film."],
          a: "This is a boring old film.",
          extra: ["That", "good", "series"],
          tip: "Opinion (boring) + age (old).",
          successTip: "US:MOVIE, UK: FILM! Both mean película! 🎥"
        },
        {
          type: "mc",
          q: "Emma sees a bottle. Which is correct?",
          options: ["a beautiful glass bottle", "a glass beautiful bottle", "a bottle beautiful glass"],
          a: "a beautiful glass bottle",
          tip: "Opinion before material.",
          successTip: "Una hermosa botella de vidrio = A BEAUTIFUL GLASS BOTTLE! 🍾"
        },
        {
          type: "order",
          q: "Traduce: 'Tenemos dos sofás morados encantadores.'",
          words: ["We", "have", "two", "lovely", "purple", "couches."],
          a: "We have two lovely purple couches.",
          extra: ["this", "ugly", "chair"],
          successTip: "NUMBER + OPINION + COLOR! Complete mastery! 🛋️✨"
        }
      ]
    },

    // ===================================================================
    // SET 6: DESCRIBING EVERYDAY THINGS (B1-)
    // ===================================================================
    {
      id: "m3-u3-set6",
      title: "Set 6: Daily Descriptions",
      context: `*Sábado por la mañana - En casa*

Emma describe las cosas que usa todos los días:
"Tengo un telefono hermoso, nuevo y negro."
"Tene una costosa bolsa de cuero."
"Mi sillon es muy comodo y suave."
"Mi auto es un carro viejo y rojo, pero funciona bien."

*Saturday morning - At home*

Emma describes the things she uses every day:
"I have a beautiful new black phone."
"I have an expensive leather bag."
"My sofa is very confortable and soft."
"My car is an old red car, but it works well."`,
      
      questions: [
        {
          type: "match",
          q: "Match the object to a good description:",
          pairs: [
            { left: "Phone", right: "A new black and smart..." },
            { left: "Bag", right: "A beautiful leather..." },
            { left: "Cup", right: "An old glass..." }
          ],
          a: "completed",
          successTip: "EVERYDAY ITEMS! Use these descriptions daily! 📱👜☕"
        },
        {
          type: "mc",
          q: "Emma describes her phone. Which is correct?",
          options: ["a nice new phone", "a new nice phone","a phone nice new"],
          a: "a nice new phone",
          tip: "Opinion (nice) before age (new)."
        },
        {
          type: "input",
          q: "Emma touches her table: 'It's a _____ _____ table.' (hermosa/de madera)",
          a: "beautiful wooden",
          alts: ["lovely wooden", "great wooden"],
          tip: "wooden = made of wood, wood=madera"
        },
        {
          type: "order",
          q: "Traduce: 'Tengo un auto viejo horrible.'",
          words: ["I", "have", "a", "horrible", "old", "car."],
          a: "I have a horrible old car.",
          extra: ["this", "ugly", "You"],
          tip: "Opinion (horrible) + age (old).",
          successTip: "HORRIBLE OLD CAR! Honest description! 🚗"
        },
        {
          type: "mc",
          q: "Emma describes a house. Which is correct?",
          options: ["a lovely big house", "a big lovely house","a house big lovely"],
          a: "a lovely big house",
          tip: "Opinion before size."
        },
        {
          type: "input",
          q: "Emma shops for shoes: 'I want _____ _____ shoes.' (comodos/negros)",
          a: "comfortable black",
          tip: "Adjectives never go in plural."
        },
        {
          type: "order",
          q: "Traduce: 'Este es un libro largo aburrido.'",
          words: ["This", "is", "a", "boring", "long", "book."],
          a: "This is a boring long book.",
          extra: ["that", "ugly", "movie"],
          tip: "Opinion (boring) + size/length (long)."
        },
        {
          type: "mc",
          q: "Emma sees a sweater. Which is correct?",
          options: ["an ugly woolen sweater", "a woolen ugly sweater","a sweater ugly woolen"],
          a: "an ugly woolen sweater",
          tip: "Opinion before material."
        },
        {
          type: "input",
          q: "Emma's friend wears: 'He wears a _____ _____ shirt.' (azul/bonita)",
          a: "nice blue",
          alts: ["great blue", "lovely blue", "beautiful blue"],
          successTip: "NICE BLUE SHIRT! Simple and perfect! 👔"
        },
        {
          type: "order",
          q: "Traduce: 'Quiero una computadora portátil plateada nueva.'",
          words: ["I", "want", "a", "new", "silver", "laptop."],
          a: "I want a new silver laptop.",
          extra: ["the", "nintendo", "green"],
          successTip: "NEW SILVER LAPTOP! Age + color! Everyday descriptions done! 💻"
        }
      ]
    },

    // ===================================================================
    // SET 7: CONTRASTS WITH 'BUT' (B1-)
    // ===================================================================
    {
      id: "m3-u3-set7",
      title: "Set 7: But...",
      context: `*Domingo por la tarde - Tienda de antigüedades*

Emma mira objetos viejos en una tienda.
"Este jarron es hermoso, pero es muy caro" dice.
El vendedor responde: "Si, pero es muy especial!"

*Sunday afternoon - Antique shop*

Emma looks at old objects in a shop.
"This vase is beautiful, but it's too expensive," she says.
The seller responds: "Yes, but it's very special!"`,
      
      questions: [
        {
          type: "match",
          q: "Match the sentence halves with 'but':",
          pairs: [
            { left: "It's expensive...", right: "but that's why I work." },
            { left: "It's old...", right: "but it looks new." },
            { left: "It's small...", right: "but it's lovely." }
          ],
          a: "completed",
          successTip: "BUT = CONTRAST! Connect opposites!! 🔄"
        },
        {
          type: "mc",
          q: "Emma looks at a house: 'The house is small, _____ it's lovely.'",
          options: ["but", "because", "and"],
          a: "but",
          tip: "BUT = contrast (small vs lovely)."
        },
        {
          type: "order",
          q: "Traduce: 'Es barato, pero es horrible.'",
          words: ["It's", "cheap,", "but", "it's", "horrible."],
          a: "It's cheap, but it's horrible.",
          extra: ["this", "ugly", "expensive"],
          tip: "Cheap (good) vs horrible (bad) = contrast.",
          successTip: "CHEAP BUT HORRIBLE! Real talk! 💬"
        },
        {
          type: "input",
          q: "Emma sees a car: 'The car is old, _____ it runs well.'",
          a: "but",
          tip: "Old vs runs well = contrast.",
          successTip: "Runs well = funciona bien! Tambien puedes decir 'works well'! 🚗"
        },
        {
          type: "mc",
          q: "Emma about a dress: 'I like it, but it's too _____.'",
          options: ["nice", "lovely", "expensive"],
          a: "expensive",
          successTip: "LIKE BUT EXPENSIVE! Common shopping problem! 👗💸"
        },
        {
          type: "order",
          q: "Traduce: 'La película es larga, pero interesante.'",
          words: ["The", "movie", "is", "long,", "but", "interesting."],
          a: "The movie is long, but interesting.",
          extra: ["good", "large", "short"],
          tip: "Large = largo pero en tamaño, Long = largo en duración.",
        },
        {
          type: "input",
          q: "Emma sees a bag: 'It's beautiful, _____ it's too big.'",
          a: "but",
          tip: "Beautiful vs too big = problem.",
          successTip: "Si usas adjetivos similares que no contrastan puedes usar 'and' (y) en vez de 'but'! 🎒"
        },
        {
          type: "mc",
          q: "Emma about a phone: 'The phone is new _____ very cheap wow!'.",
          options: ["but", "because", "and"],
          a: "and",
          tip: "New and cheap = Nuevo y barato, ambos son positivos!",
          successTip: "NEW and CHEAP! What a bargain! 📱💔"
        },
        {
          type: "order",
          q: "Traduce: 'Es feo, pero es barato.'",
          words: ["It's", "ugly,", "but", "it's", "cheap."],
          a: "It's ugly, but it's cheap.",
          extra: ["nice", "horrible", "expensive"],
          tip: "Ugly vs cheap = justification."
        },
        {
          type: "input",
          q: "Emma about a friend: 'She's _____, but she talks too much.' (agradable)",
          a: "nice",
          alts: ["lovely", "great", "kind", "adorable","pleasant"],
          successTip: "NICE BUT...! Perfect contrast! Now: reasons! 💬"
        }
      ]
    },

    // ===================================================================
    // SET 8: REASONS WITH 'BECAUSE' (B1-)
    // ===================================================================
    {
      id: "m3-u3-set8",
      title: "Set 8: Because...",
      context: `*Lunes por la mañana - Cafetería*

Emma explica sus decisiones a su amiga:
"Me encanta esta cafetería porque el cafe es delicioso."
"No me gusta esa tienda porque es muy cara."

*Monday morning - Coffee shop*

Emma explains her decisions to her friend:
"I love this café because the coffee is delicious."
"I don't like that shop because it's too expensive."`,
      
      questions: [
        {
          type: "match",
          q: "Match opinions to reasons:",
          pairs: [
            { left: "I like it...", right: "because it's beautiful." },
            { left: "I hate it...", right: "because it's ugly." },
            { left: "I want it...", right: "because I love it." }
          ],
          a: "completed",
          successTip: "BECAUSE = REASON! Explain WHY! Opinion + because + reason! 💡"
        },
        {
          type: "mc",
          q: "Emma explains: 'I love this dress _____ it's beautiful.'",
          options: ["because", "but", "and"],
          a: "because",
          tip: "BECAUSE = reason (why I love it)."
        },
        {
          type: "order",
          q: "Traduce: 'Lo quiero porque es barato.'",
          words: ["I", "want", "it", "because", "it's", "cheap."],
          a: "I want it because it's cheap.",
          extra: ["but", "and"],
          tip: "Because introduces the reason.",
          successTip: "I WANT IT BECAUSE IT'S CHEAP! Smart reasoning! 🛍️"
        },
        {
          type: "input",
          q: "Emma about a car: 'I like this car _____ it's fast.'",
          a: "because",
          tip: "Fast = reason for liking."
        },
        {
          type: "mc",
          q: "Emma dislikes a movie: 'She hates it, _____ her friends like it.'",
          options: ["because", "but"],
          a: "but",
          tip: "Contrasting opinions.",
          successTip: "HATES IT BUT HER FRIENDS LIKE IT! Contrasting opinions! 🎬"
        },
        {
          type: "order",
          q: "Traduce: 'No me gusta porque es feo.'",
          words: ["I", "don't", "like", "it", "because", "it's", "ugly."],
          extra: ["but", "and"],
          a: "I don't like it because it's ugly.",
          tip: "Because + reason for not liking."
        },
        {
          type: "input",
          q: "Emma and friends: 'We love it _____ it's fun.'",
          a: "because",
          tip: "Fun = reason for loving.",
          successTip: "A veces puedes usar 'so' para decir el resultado 'It's fun, so we love it!' 🎉"
        },
        {
          type: "mc",
          q: "Emma wants something: 'It's the ____ Iphone, so I want it!'",
          options: ["new", "old","expensive"],
          a: "new",
          successTip: "Because te da la causa y so el resultado! 🆕"
        },
        {
          type: "order",
          q: "Traduce: 'Lo odio porque es horrible y aburrido'",
          words: ["I", "hate", "it", "because", "it's", "horrible", "and", "boring."],
          a: "I hate it because it's horrible and boring.",
          extra: ["but", "so","because"],
          tip: "Horrible = strong reason for hating."
        },
        {
          type: "input",
          q: "Emma and family: 'They like it _____ it's comfortable.'",
          a: "because",
          successTip: "BECAUSE IT'S COMFORTABLE! Perfect reason! Next: real shopping! 🛋️"
        }
      ]
    },

    // ===================================================================
    // SET 9: REAL SHOPPING DESCRIPTIONS (B1-)
    // ===================================================================
    {
      id: "m3-u3-set9",
      title: "Set 9: Shopping Decisions",
      context: `*Martes - Centro comercial con Lucy*

Emma y Lucy están de compras. Emma ve un vestido:
"Me gusta este vestido, pero es muy caro."
Lucy sugiere: "Busquemos algo bonito y barato!"

*Tuesday - Shopping mall with Lucy*

Emma and Lucy are shopping. Emma sees a dress:
"I love this dress, but it's too expensive."
Lucy suggests: "Let's find something nice and cheap!"`,
      
      questions: [
        {
          type: "match",
          q: "Match shopping opinions:",
          pairs: [
            { left: "Too expensive!", right: "I don't want it." },
            { left: "Beautiful but expensive", right: "I like it, but..." },
            { left: "Cheap and nice!", right: "Perfect!" }
          ],
          a: "completed",
          successTip: "REAL SHOPPING! These phrases are essential! Use them! 🛒💬"
        },
        {
          type: "mc",
          q: "Emma at a store: 'I love this dress, _____ it's too expensive.'",
          options: ["but", "because","so"],
          a: "but",
          tip: "Love vs expensive = contrast."
        },
        {
          type: "order",
          q: "Traduce: 'Lo quiero porque es hermoso y barato.'",
          words: ["I", "want", "it", "because", "it's", "beautiful", "and", "cheap."],
          a: "I want it because it's beautiful and cheap.",
          tip: "Beautiful = reason to buy.",
          successTip: "I WANT IT BECAUSE IT'S BEAUTIFUL! Honest shopping! 💳"
        },
        {
          type: "input",
          q: "Emma tries on a sweater: 'This sweater is nice, _____ I want it' (entonces)",
          a: "so",
          tip: "So = result.",
          successTip: "Domina 'so', 'because', 'but', 'and' en la seccion de gramatica del modulo 2"
        },
        {
          type: "mc",
          q: "Emma about shoes: 'I like these shoes _____ they're comfortable.'",
          options: ["because", "but", "so"],
          a: "because",
          successTip: "COMFORTABLE = great reason! Perfect for shoes! 👠"
        },
        {
          type: "order",
          q: "Traduce: 'Es encantador, pero es muy pequeño para mi.'",
          words: ["It's", "lovely,", "but", "it's", "too", "small", "for", "me."],
          a: "It's lovely, but it's too small for me.",
          tip: "Lovely vs too small = contrast."
        },
        {
          type: "input",
          q: "Emma rejects something: 'I don't want it _____ it's ugly ____ very big.'",
          a: "because and",
          tip: "Ugly and big= reason for not wanting."
        },
        {
          type: "mc",
          q: "Emma finds perfect item: 'This cheap and beautiful, ___ it's perfect.!'",
          options: ["because", "but","so"],
          a: "so",
          successTip: "CHEAP AND BEAUTIFUL! Best combination! 🎯"
        },
        {
          type: "order",
          q: "Traduce: 'Lo odio porque es aburrido.'",
          words: ["I", "hate", "it", "because", "it's", "boring."],
          a: "I hate it because it's boring.",
          extra: ["but", "so","and"],
          tip: "Boring = reason for hating."
        },
        {
          type: "input",
          q: "Emma justifies a purchase: 'It's expensive, _____ it's good quality.'",
          a: "but",
          successTip: "EXPENSIVE BUT GOOD QUALITY! Smart justification! 💎"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTER DESCRIPTIONS (B1-)
    // ===================================================================
    {
      id: "m3-u3-set10",
      title: "Set 10: Description Master",
      context: `*Miércoles - Última lección de Emma*

Emma practica hacer descripciones completas:
"Tengo una vieja mesa de madera encantadora."
"Me encanta porque es una hermosa antigüedad"
"Fue caro, pero es perfecta para mi casa!"

*Wednesday - Emma's final lesson*

Emma practices making complete descriptions:
"I have a lovely old wooden table."
"I love it because it's a beautiful antique."
"It was expensive, but it's perfect for my house!"`,
      
      questions: [
        {
          type: "match",
          q: "Match complete descriptions:",
          pairs: [
            { left: "My phone", right: "is beautiful, new, black and it has 1Tb of storage." },
            { left: "My car", right: "Is very old and red, but it works." },
            { left: "My bag", right: "is expensive and made of leather." }
          ],
          a: "completed",
          successTip: "1Tb storage = 1Tb de almacenamiento! 🎓"
        },
        {
          type: "order",
          q: "Traduce: 'Tengo una mesa de madera vieja encantadora.'",
          words: ["I", "have", "a", "lovely", "old", "wooden", "table."],
          a: "I have a lovely old wooden table.",
          extra: ["and", "because", "but"],
          tip: "Opinion (lovely) + age (old) + material (wooden).",
          successTip: "PERFECT! Opinion + age + material! Complete! 🪵✨"
        },
        {
          type: "mc",
          q: "Emma describes a bag. Which is the best complete description?",
          options: ["a horrible green plastic bag", "a plastic horrible green bag", "a green plastic horrible bag"],
          a: "a horrible green plastic bag",
          tip: "Opinion (horrible) + color (green) + material (plastic)."
        },
        {
          type: "input",
          q: "Emma wants a dress: 'I want a _____ _____ _____ dress.' (hermoso, largo, azul)",
          a: "beautiful long blue",
          tip: "Opinion (beautiful) + size (long) + color (blue)."
        },
        {
          type: "order",
          q: "Traduce: 'Es caro, pero es hermoso.'",
          words: ["It's", "expensive,", "but", "it's", "beautiful."],
          a: "It's expensive, but it's beautiful.",
          extra: ["and", "because", "but"],
          tip: "Use but for contrast."
        },
        {
          type: "mc",
          q: "Emma gives a complete reason. Which is correct?",
          options: ["I love it because it's a beautiful red dress.", "I love it but it's a beautiful red dress."],
          a: "I love it because it's a beautiful red dress.",
          successTip: "BECAUSE = REASON! Perfect logic! 💕"
        },
        {
          type: "input",
          q: "Emma explains: 'I don't like it _____ it's an ugly old car.'",
          a: "because",
          tip: "Because = reason for not liking."
        },
        {
          type: "order",
          q: "Traduce: 'Lo quiero, pero es muy caro.'",
          words: ["I", "want", "it,", "but", "it's", "too", "expensive."],
          a: "I want it, but it's too expensive.",
          extra: ["and", "because", "but"],
          tip: "Want vs expensive = conflict."
        },
        {
          type: "mc",
          q: "Final test! Which is COMPLETELY correct?",
          options: ["She has a beautiful new leather jacket.", "She has a leather beautiful new jacket."],
          a: "She has a beautiful new leather jacket.",
          tip: "Opinion (beautiful) + age (new) + material (leather).",
          successTip: "PERFECT! Beautiful new leather jacket! Complete mastery! 🧥🏆"
        },
        {
          type: "input",
          q: "Emma's final description: 'My laptop is old and slow, ____ I want a new one.'",
          a: "so",
          successTip: "🏆 UNIT COMPLETE! You're a DESCRIPTION MASTER! Opinion, order, contrast, reasons! Next: Reading! 📖"
        }
      ]
    }
  ],

  readingSet: {
  id: "m3-u3-reading",
  title: "Reading: Shopping for Quality",
  category: "reading",

  questions: [
    {
      type: "passage",
      passage: `**At the Clothing Store**

I am at a clothing store today. I want to buy a new dress.

I see a beautiful red dress on the wall. It is lovely! But the price is 200 euros. That is too expensive for me. I cannot buy it.

Then I see a blue dress. It is cheaper - only 80 euros. It is beautiful and comfortable. I like this dress! I want to buy this one.`
    },
    
    {
      type: "mc",
      q: "What dress does she see first?",
      options: ["A beautiful red dress", "A blue dress", "A green dress"],
      a: "A beautiful red dress",
      tip: "Check the first dress mentioned.",
      successTip: "✓ RED DRESS FIRST! She likes it, but the price is a problem! 👗"
    },
    
    {
      type: "input",
      q: "Why can't she buy the red dress? -Because it is too _____💰",
      a: "expensive",
      alts: ["costly"],
    },
    
    {
      type: "mc",
      q: "What color dress does she want to buy?",
      options: ["Blue", "Red", "Green"],
      a: "Blue",
      tip: "Check the second paragraph.",
      successTip: "✓ BLUE! She finds a great alternative! 💙"
    },

    {
      type: "passage",
      passage: `**The Furniture Store**

Now I am in a furniture store. I need a new table for my kitchen.

There is a horrible plastic table here. It is very cheap - only 50 euros. But it is ugly! I do not want this table.

There is also a beautiful wooden table. It is more expensive - 300 euros. But it is perfect for my kitchen! I love this table. I want to buy it.`
    },
    
    {
      type: "mc",
      q: "What is the horrible table made of?",
      options: ["Plastic", "Wood", "Metal"],
      a: "Plastic",
      tip: "'A horrible plastic table.'",
      successTip: "✓ PLASTIC! Cheap but ugly! 🏗️"
    },
    
    {
      type: "input",
      q: "She wants to buy the ___ table.",
      a: "wooden",
      alts: ["wood"],
      tip: "'A beautiful wooden table.'",
      successTip: "✓ WOODEN! Better quality! 🪵✨"
    },
    
    {
      type: "mc",
      q: "Why does she want the wooden table?",
      options: ["It is perfect for her kitchen", "It is cheap", "her friends like it"],
      a: "It is perfect for her kitchen",
      tip: "'It is perfect...'",
      successTip: "✓ PERFECT FIT! Good reason to spend more! 🎯"
    },

    {
      type: "passage",
      passage: `**More Shopping**

I also need a new bag. I want a leather bag, but all the leather bags here are too expensive. They are 150 euros!

I see a lovely black fabric bag. It is not leather, but it is nice. And it is cheap - only 40 euros! This is a good bag.

Finally, I go to an electronics store. There is a beautiful silver laptop here. But it is very expensive - 1,200 euros! That is too much money. Maybe next month!`
    },
    
    {
      type: "mc",
      q: "What kind of bag does she want?",
      options: ["Leather", "Fabric", "Plastic"],
      a: "Leather",
      tip: "'I want a leather bag...'",
      successTip: "✓ LEATHER! But too expensive! 👜"
    },
    
    {
      type: "input",
      q: "She sees the _____ _____ bag.",
      a: "black fabric",
      alts: ["fabric black"],
      tip: "'A lovely black fabric bag.'",
      successTip: "✓ BLACK FABRIC! Good alternative! ⬛"
    },
    
    {
      type: "mc",
      q: "Does she want to buy the laptop today?",
      options: ["No", "Yes"],
      a: "No",
      tip: "'Maybe next month!'",
      successTip: "✓ NOT TODAY! Sometimes we wait for better prices! 💻"
    },
    
    {
      type: "mc",
      q: "Why can't she buy the laptop?",
      options: ["Too expensive", "Ugly", "Too big"],
      a: "Too expensive",
      tip: "'That is too much money.'",
      successTip: "🏆 READING COMPLETE! You understood all descriptions perfectly! Well done! 🎉"
    }
  ]
},


  // ===================================================================
  // REVIEW SET (10 PREGUNTAS)
  // ===================================================================
  reviewSet: {
    id: "m3-u3-review",
    title: "Unit 3 Review",
    category: "review",
    questions: [
      {
        type: "mc",
        q: "Choose a positive opinion adjective:",
        options: ["beautiful", "ugly", "boring"],
        a: "beautiful",
        tip: "Beautiful = hermoso (positive)."
      },
      {
        type: "input",
        q: "My shirt is _____ 🤮, but it's a gift, so I wear it anyway.",
        a: "ugly",
        alts: ["horrible", "bad", "ugly"],
        tip: "Ugly = feo."
      },
      {
        type: "mc",
        q: "Which sentence is correct?",
        options: ["a lovely green hat", "a green lovely hat", "a green hat lovely"],
        a: "a lovely green hat",
        tip: "Opinion before fact."
      },
      {
        type: "order",
        q: "Traduce: 'Es un vestido rojo hermoso.'",
        words: ["It's", "a", "beautiful", "red", "dress."],
        a: "It's a beautiful red dress.",
        extra: ["blue", "skirt", "ugly"],
        tip: "Opinion (beautiful) + color (red)."
      },
      {
        type: "input",
        q: "A ___ table is better than a plastic one.",
        a: "wooden",
        tip: "Wood → wooden.",
        successTip: "Better than = mejor que."
      },
      {
        type: "mc",
        q: "My car seems old, ___ it works well.",
        options: ["but", "because", "so"],
        a: "but",
        tip: "But = contrast.",
        successTip: "Seem = parecer."
      },
      {
        type: "order",
        q: "Traduce: 'Me gusta porque es bonito y azul.'",
        words: ["I", "like", "it", "because", "it's", "nice", "and", "blue."],
        a: "I like it because it's nice and blue.",
        extra: ["ugly", "he"],
        tip: "Because = reason."
      },
      {
        type: "input",
        q: "'It's expensive, _____ horrible.😵 I don't want it'",
        a: "and",
        tip: "And for addition."
      },
      {
        type: "mc",
        q: "Best complete description:",
        options: ["a horrible old car", "an old horrible car", "a car old horrible"],
        a: "a horrible old car",
        tip: "Opinion (horrible) + age (old)."
      },
      {
        type: "order",
        q: "Traduce: 'Ese es un vestido azul encantador.'",
        words: ["That", "is", "a", "lovely", "blue", "dress."],
        a: "That is a lovely blue dress.",
        extra: ["awful", "This"],
        successTip: "🏆 UNIT 3 COMPLETE! You can describe anything perfectly! Ready for Module 4! 🎉"
      }
    ]
  }
};

